import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { SectionHeading, Reveal } from "@/components/Section";
import { Trophy } from "lucide-react";

const CHIP_STYLE = {
  COVERED: "border-volt/60 bg-volt/10 text-volt",
  OVER: "border-volt/60 bg-volt/10 text-volt",
  LOST: "border-liversed/60 bg-liversed/10 text-liversed",
  UNDER: "border-bline bg-ink text-slate-300",
  PUSH: "border-bline bg-ink text-slate-400",
};

function OddsChip({ data, testid }) {
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 rounded-sm border px-2 py-1 font-score text-[10px] font-bold tracking-wider ${CHIP_STYLE[data.result] || CHIP_STYLE.PUSH}`}
    >
      {data.label} · {data.result}
    </span>
  );
}

function Linescore({ event }) {
  const a = event.away.lines || [];
  const h = event.home.lines || [];
  const periods = Math.max(a.length, h.length);
  if (periods === 0) return null;
  return (
    <table className="mt-3 w-full border-collapse font-score text-[11px]" data-testid="result-linescore">
      <thead>
        <tr className="text-slate-500">
          <th className="w-10 pb-1 text-left font-normal" />
          {Array.from({ length: periods }, (_, i) => (
            <th key={i} className="pb-1 text-center font-normal">{i + 1}</th>
          ))}
          <th className="pb-1 text-center font-normal text-white">T</th>
        </tr>
      </thead>
      <tbody>
        {[[event.away, a], [event.home, h]].map(([side, lines]) => (
          <tr key={side.abbr} className={side.winner ? "text-white" : "text-slate-400"}>
            <td className="py-0.5 font-bold">{side.abbr}</td>
            {Array.from({ length: periods }, (_, i) => (
              <td key={i} className="py-0.5 text-center">{lines[i] ?? "–"}</td>
            ))}
            <td className="py-0.5 text-center font-bold text-white">{side.score}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ResultCard({ r }) {
  const e = r.event;
  const t = r.team;
  return (
    <Reveal>
      <Link
        to={`/team/${t.slug}`}
        data-testid={`result-card-${t.slug}`}
        className="group block rounded-xl border border-bline bg-surface p-5 transition-all duration-200 hover:-translate-y-0.5 hover:border-volt/50"
      >
        <div className="mb-3 flex items-center gap-2">
          <span className="rounded-sm bg-volt/10 px-1.5 py-0.5 font-score text-[9px] font-bold tracking-widest text-volt">{t.league}</span>
          <span className="font-score text-[9px] tracking-widest text-liversed">FINAL</span>
          {e.venue && <span className="ml-auto truncate font-score text-[9px] text-slate-500">{e.venue}</span>}
        </div>
        <div className="space-y-2">
          {[e.away, e.home].map((side) => (
            <div key={side.abbr} className="flex items-center gap-2.5">
              {side.logo && <img src={side.logo} alt="" className="h-7 w-7 shrink-0 object-contain" />}
              <span className={`min-w-0 flex-1 truncate text-sm font-bold ${side.winner ? "text-white" : "text-slate-400"}`}>
                {side.name}
                {side.name === t.name && <span className="ml-1.5 font-score text-[9px] tracking-widest text-volt">DFW</span>}
              </span>
              <span className={`font-score text-2xl ${side.winner ? "text-volt" : "text-slate-400"}`}>{side.score}</span>
            </div>
          ))}
        </div>
        <Linescore event={e} />
        {r.leader && (
          <p className="mt-3 flex items-center gap-1.5 text-xs text-slate-400" data-testid={`result-leader-${t.slug}`}>
            <Trophy className="h-3.5 w-3.5 shrink-0 text-volt" />
            <span><strong className="text-white">{r.leader.name}</strong> ({r.leader.team}) — {r.leader.value} · {r.leader.category}</span>
          </p>
        )}
        {r.team_stats.length > 0 && (
          <div className="mt-3 space-y-1 border-t border-bline pt-3" data-testid={`result-stats-${t.slug}`}>
            {r.team_stats.map((s) => (
              <div key={s.label} className="flex items-baseline font-score text-[11px]">
                <span className="w-14 text-white">{s.away}</span>
                <span className="flex-1 text-center text-[9px] tracking-widest text-slate-500">{String(s.label).toUpperCase()}</span>
                <span className="w-14 text-right text-white">{s.home}</span>
              </div>
            ))}
            <div className="flex font-score text-[8px] tracking-widest text-slate-600">
              <span className="w-14">{e.away.abbr}</span>
              <span className="flex-1" />
              <span className="w-14 text-right">{e.home.abbr}</span>
            </div>
          </div>
        )}
        {r.odds_result && (
          <div className="mt-3 flex flex-wrap gap-2 border-t border-bline pt-3" data-testid={`result-odds-${t.slug}`}>
            {r.odds_result.spread && <OddsChip data={r.odds_result.spread} testid={`result-odds-spread-${t.slug}`} />}
            {r.odds_result.total && <OddsChip data={r.odds_result.total} testid={`result-odds-total-${t.slug}`} />}
          </div>
        )}
      </Link>
    </Reveal>
  );
}

export default function TodayResults() {
  const [day, setDay] = useState("today");
  const [data, setData] = useState(null);
  const [checked, setChecked] = useState(false);

  const fetchDay = (d) => api.get(`/results/${d}`).then(({ data }) => data).catch(() => ({ results: [] }));

  useEffect(() => {
    (async () => {
      const today = await fetchDay("today");
      if (today.results.length > 0) {
        setDay("today");
        setData(today);
      } else {
        // after midnight rollover, last night's finals are what fans want first
        const yday = await fetchDay("yesterday");
        if (yday.results.length > 0) {
          setDay("yesterday");
          setData(yday);
        } else {
          setData({ results: [] });
        }
      }
      setChecked(true);
    })();
  }, []);

  const switchDay = async (d) => {
    if (d === day) return;
    setDay(d);
    setData(null);
    setData(await fetchDay(d));
  };

  if (!checked || !data || (data.results || []).length === 0) return null;

  return (
    <section id="results" className="mx-auto max-w-7xl scroll-mt-24 px-4 py-20 sm:px-8 lg:px-12" data-testid="results-section">
      <div className="flex items-end justify-between gap-4">
        <SectionHeading index="02" eyebrow="IN THE BOOKS" title={day === "today" ? "Today's Results" : "Last Night's Results"} />
        <div className="mb-8 flex shrink-0 gap-1 rounded-sm border border-bline bg-surface p-1" data-testid="results-tabs">
          {[["today", "TODAY"], ["yesterday", "YESTERDAY"]].map(([d, label]) => (
            <button
              key={d}
              data-testid={`results-tab-${d}`}
              onClick={() => switchDay(d)}
              className={`rounded-sm px-3 py-1.5 font-display text-sm tracking-widest transition-colors ${day === d ? "bg-volt text-ink" : "text-slate-400 hover:text-white"}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {(data.results || []).map((r) => (
          <ResultCard key={`${r.team.slug}-${r.event.id}`} r={r} />
        ))}
      </div>
    </section>
  );
}
